import "./home.css";
import caConnectLogo from "../images/ca_connect.png";
import { useEffect, useRef, useState } from "react";
import Login from "../Chat/Login/login";
import LoginV3 from "../Chat/Login/login-v3";
import Chat from '../Chat/chat';
import CharacterSelection from './CharacterSelection';
import FirmDashboard from '../Firm/FirmDashboard';
import { apiCall } from '../utils/api';
import { MessageSquare, Shield, Star } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const Home = ({ darkMode, toggleDarkMode }) => {
    const [isAndroidSource, setIsAndroidSource] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const initialViewportHeightRef = useRef(null);

    useEffect(() => {
        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        const paramsObj = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });

        // Check if UTM source is android
        const utmSource = paramsObj.source;
        setIsAndroidSource(utmSource === 'android');

        if (paramsObj && Object.keys(paramsObj).length > 0) {
            localStorage.setItem('signupSource', JSON.stringify(paramsObj));
        }

    }, []);

    // Keyboard-aware behavior (Android WebView): hide bottom trust section when keyboard is open
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        if (initialViewportHeightRef.current == null) {
            initialViewportHeightRef.current = vv.height;
        }

        const updateKeyboardState = () => {
            const baseline = initialViewportHeightRef.current ?? vv.height;
            const keyboardHeightEstimate = baseline - vv.height;
            setIsKeyboardOpen(keyboardHeightEstimate > 150);
        };

        const handleOrientationChange = () => {
            initialViewportHeightRef.current = vv.height;
            updateKeyboardState();
        };

        updateKeyboardState();
        vv.addEventListener('resize', updateKeyboardState);
        vv.addEventListener('scroll', updateKeyboardState);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            vv.removeEventListener('resize', updateKeyboardState);
            vv.removeEventListener('scroll', updateKeyboardState);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isUnidentifiedUser, setIsUnidentifiedUser] = useState(false);
    const [error, setError] = useState(null);
    const [userType, setUserType] = useState(() => localStorage.getItem('user_type') || 'user');

    const navigate = useNavigate();

    useEffect(() => {
        const checkLogin = async () => {
            try {
                const data = await apiCall.get('/users/check/login');
                const loggedIn = data?.data?.is_logged_in;
                setIsLoggedIn(loggedIn);
            } catch (error) {
                if (error.message === 'Refresh token expired') {
                    setIsLoggedIn(false);
                } else {
                    setError(error);
                }
            } finally {
                setIsLoading(false);
            }
        };
        checkLogin();
    }, []);

    useEffect(() => {
        if (!isLoggedIn) return;
        const redirectPath = localStorage.getItem('postLoginRedirect');
        if (redirectPath) {
            localStorage.removeItem('postLoginRedirect');
            navigate(redirectPath, { replace: true });
        }
    }, [isLoggedIn, navigate]);

    const handleStartChat = () => {
        // This will be handled by the CharacterSelection component
    };



    // Throw error during render to trigger error boundary
    if (error) {
        throw error;
    }

    if (isLoading) {
        return null;
    }

    const handleFirmLogout = () => {
        setIsLoggedIn(false);
        setUserType('user');
    };

    const handleFirmLoginSuccess = (type) => {
        setUserType(type);
        setIsLoggedIn(true);
    };

    // If logged in, show appropriate screen based on user type
    if (isLoggedIn) {
        if (userType === 'firm') {
            return (
                <FirmDashboard onLogout={handleFirmLogout} />
            );
        }
        return (
            <CharacterSelection
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
            />
        );
    }
    
    return (<div className={`Home${isKeyboardOpen ? ' keyboard-open' : ''}`}>
        {/* Blue header section with logo */}
        <div className="home-header-section">
            <div className="home-logo-stack">
                <img src={caConnectLogo} alt="CA Connect" className="home-logo-image" />
                <div className="home-logo-text">CA Connect</div>
            </div>
        </div>

        {/* White rounded section with login */}
        <div className="home-white-section">
            <div className="home-content">
                <div className="home-login-area">
                    <LoginV3 darkMode={darkMode} toggleDarkMode={toggleDarkMode} onFirmLoginSuccess={handleFirmLoginSuccess} />
                </div>

                {/* Bottom strip (trust + terms) */}
                <div className="home-footer">
                    <div className="home-trust-line">Private &amp; secure. Your data is safe with us.</div>

                    <div className="home-terms">
                        <div>
                            <a href="https://www.chaimate.ai/terms-of-service" className="terms-link" target="_blank" rel="noopener noreferrer">Terms of Service</a> &nbsp;•&nbsp; <a href="https://www.chaimate.ai/privacy-policy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>);
};

export default Home;